import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Endpoints } from "../shared/config/endpoints";
import { GeneralService } from "./general.service";

/**
 * Feature flags are operational rollout controls, separate from
 * licenses, modules, resources, roles, and permissions.
 *
 * Use `GET /feature-flags/evaluate?tenantID={tenantID}` after the
 * tenant context is known. The backend returns the effective boolean
 * for each feature code (tenant override wins, global value falls back).
 */
export type FeatureFlagMap = Record<string, boolean>;

@Injectable({
  providedIn: "root",
})
export class FeatureFlagsService {
  private featureFlagsSubject = new BehaviorSubject<FeatureFlagMap>({});
  private loadedSubject = new BehaviorSubject<boolean>(false);
  private activeTenantID: string = null;
  private loadedTenantID: string = null;
  public featureFlags$ = this.featureFlagsSubject.asObservable();
  public loaded$ = this.loadedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private endpoints: Endpoints,
    private gs: GeneralService,
  ) {}

  /**
   * Fetch the evaluated feature flags for the given tenant.
   * Caches the result in a BehaviorSubject.
   */
  evaluate(tenantID: string | number): Observable<FeatureFlagMap> {
    const tenantKey = `${tenantID}`;
    const url = `${this.endpoints.adminserviceUrl}/feature-flags/evaluate?tenantID=${tenantID}`;
    const headers = this.gs.getCommandCenterHeaders();

    if (this.activeTenantID !== tenantKey) {
      this.activeTenantID = tenantKey;
      this.loadedTenantID = null;
      this.loadedSubject.next(false);
      this.featureFlagsSubject.next({});
    }

    const observable = this.http.get<FeatureFlagMap>(url, { headers });
    observable.subscribe({
      next: (flags) => {
        if (this.activeTenantID !== tenantKey) return;
        this.loadedTenantID = tenantKey;
        this.featureFlagsSubject.next(flags || {});
        this.loadedSubject.next(true);
      },
      error: () => {
        if (this.activeTenantID !== tenantKey) return;
        this.loadedTenantID = tenantKey;
        // Default to empty map on error so all features are disabled
        this.featureFlagsSubject.next({});
        this.loadedSubject.next(true);
      },
    });
    return observable;
  }

  /**
   * Check if a feature is enabled (synchronous, current value).
   * Unknown or missing feature codes default to `false`.
   */
  isFeatureEnabled(code: string): boolean {
    const flags = this.featureFlagsSubject.value;
    if (!flags) return false;
    return flags[code] === true;
  }

  /**
   * Get the current evaluated feature flags map.
   */
  getCurrentFlags(): FeatureFlagMap {
    return this.featureFlagsSubject.value || {};
  }

  hasLoadedCurrentTenantFlags(): boolean {
    return !!this.activeTenantID && this.loadedTenantID === this.activeTenantID;
  }

  /**
   * Observable that emits true when the feature flag becomes enabled.
   * Useful for templates that need to react asynchronously.
   */
  isFeatureEnabled$(code: string): Observable<boolean> {
    return this.featureFlags$.pipe(map((flags) => flags[code] === true));
  }

  /**
   * Reset the cached feature flags.
   */
  reset(): void {
    this.activeTenantID = null;
    this.loadedTenantID = null;
    this.loadedSubject.next(false);
    this.featureFlagsSubject.next({});
  }
}
